<template>
  <div class="province-map-selector">
    <!-- 触发按钮 -->
    <el-button @click="openDialog" type="primary">
      {{ selectedProvince ? selectedProvince.name : '选择省份' }}
    </el-button>

    <!-- 弹窗 -->
    <el-dialog
      title="选择省份"
      :visible.sync="dialogVisible"
      width="80%"
      top="5vh"
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <div class="map-container">
        <!-- 地图容器 -->
        <div ref="mapChart" class="map-chart"></div>
        
        <!-- 选中的省份显示 -->
        <div v-if="currentSelected" class="selected-info">
          <el-tag type="success" size="medium">
            已选择：{{ currentSelected.name }}
          </el-tag>
          <el-button 
            type="primary" 
            size="small" 
            @click="confirmSelect"
            class="confirm-btn"
          >
            确认选择
          </el-button>
        </div>
        
        <!-- 提示信息 -->
        <div class="hint-info">
          <el-alert
            title="提示：点击地图上的省份进行选择"
            type="info"
            :closable="false"
            show-icon
          />
        </div>
      </div>
      
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="confirmSelect"
          :disabled="!currentSelected"
        >
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'ProvinceMapSelector',
  
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      dialogVisible: false,
      mapChart: null,
      currentSelected: null,
      selectedProvince: null,
      isMapLoaded: false,
      
      mapOption: {
        title: {
          text: '中国地图',
          left: 'center',
          textStyle: {
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            return params.name
          }
        },
        visualMap: {
          show: false,
          min: 0,
          max: 100,
          left: 'left',
          top: 'bottom',
          text: ['高', '低'],
          calculable: true,
          seriesIndex: [0],
          inRange: {
            color: ['#e0f3ff', '#1890ff']
          }
        },
        geo: {
          map: 'china',
          roam: true,
          zoom: 1,
          center: [105, 36],
          label: {
            emphasis: {
              show: false
            }
          },
          itemStyle: {
            normal: {
              areaColor: '#f3f3f3',
              borderColor: '#999',
              borderWidth: 1
            },
            emphasis: {
              areaColor: '#1890ff',
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              shadowBlur: 20,
              borderWidth: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        },
        series: []
      }
    }
  },
  
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const province = Object.values(provinceData).find(p => p.id === newVal)
          if (province) {
            this.selectedProvince = { ...province }
          }
        }
      }
    }
  },
  
  methods: {
    // 打开弹窗
    async openDialog() {
      this.dialogVisible = true
      this.$nextTick(async () => {
        await this.initMap()
        // 如果有已选中的省份，在地图上高亮显示
        if (this.selectedProvince) {
          this.highlightProvince(this.selectedProvince.name)
        }
      })
    },
    
    // 初始化地图 - 使用异步加载
    async initMap() {
      if (!this.$refs.mapChart) return
      
      if (this.mapChart) {
        this.mapChart.dispose()
      }
      
      this.mapChart = echarts.init(this.$refs.mapChart)
      
      try {
        // 方法1：使用 echarts 自带的中国地图（推荐）
        // ECharts 5.x 自带中国地图，无需额外引入
        this.mapOption.geo.map = 'china'
        
        // 方法2：如果你安装了 echarts-china-provinces-js，可以这样使用
        // import('echarts-china-provinces-js')
        //   .then(() => {
        //     this.mapChart.setOption(this.mapOption)
        //   })
        
        this.mapChart.setOption(this.mapOption)
        
        // 绑定点击事件
        this.mapChart.on('click', this.handleMapClick)
        
        // 自适应大小
        window.addEventListener('resize', this.handleResize)
        this.handleResize()
        
        this.isMapLoaded = true
        
      } catch (error) {
        console.error('地图初始化失败:', error)
        this.$message.error('地图加载失败，请刷新重试')
        // 降级方案：显示省份选择下拉框
        this.showFallbackSelector()
      }
    },
    
    // 降级方案：显示下拉选择器
    showFallbackSelector() {
      this.$confirm('地图加载失败，是否使用下拉选择省份？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.showProvinceSelectDialog()
      }).catch(() => {
        this.dialogVisible = false
      })
    },
    
    // 显示省份选择下拉框
    showProvinceSelectDialog() {
      this.$prompt('请选择省份', '省份选择', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /.+/,
        inputErrorMessage: '请选择省份',
        inputType: 'select',
        inputOptions: this.getProvinceOptions(),
        inputValue: this.selectedProvince ? this.selectedProvince.id : ''
      }).then(({ value }) => {
        const province = Object.values(provinceData).find(p => p.id === value)
        if (province) {
          this.confirmProvinceSelect(province)
        }
      }).catch(() => {
        // 用户取消
      })
    },
    
    // 获取省份选项
    getProvinceOptions() {
      const options = {}
      Object.values(provinceData).forEach(province => {
        options[province.id] = province.name
      })
      return options
    },
    
    // 地图点击事件
    handleMapClick(params) {
      if (params.componentType === 'geo') {
        const provinceName = params.name
        const province = provinceData[provinceName]
        
        if (province) {
          this.currentSelected = { ...province }
          this.highlightProvince(provinceName)
        }
      }
    },
    
    // 高亮显示选中的省份
    highlightProvince(provinceName) {
      if (!this.mapChart || !this.isMapLoaded) return
      
      try {
        const option = this.mapChart.getOption()
        
        // 重置所有省份样式
        option.geo.itemStyle = {
          normal: {
            areaColor: '#f3f3f3',
            borderColor: '#999',
            borderWidth: 1
          },
          emphasis: {
            areaColor: '#1890ff'
          }
        }
        
        // 添加选中效果
        option.geo.regions = option.geo.regions || []
        const existingRegionIndex = option.geo.regions.findIndex(r => r.name === provinceName)
        
        if (existingRegionIndex >= 0) {
          option.geo.regions[existingRegionIndex].itemStyle = {
            normal: {
              areaColor: '#ff6b6b',
              borderColor: '#ff0000',
              borderWidth: 2
            }
          }
        } else {
          option.geo.regions.push({
            name: provinceName,
            itemStyle: {
              normal: {
                areaColor: '#ff6b6b',
                borderColor: '#ff0000',
                borderWidth: 2
              }
            }
          })
        }
        
        this.mapChart.setOption(option)
        
      } catch (error) {
        console.error('高亮省份失败:', error)
      }
    },
    
    // 确认选择
    confirmSelect() {
      if (this.currentSelected) {
        this.confirmProvinceSelect(this.currentSelected)
      }
    },
    
    // 确认省份选择
    confirmProvinceSelect(province) {
      this.selectedProvince = { ...province }
      
      // 触发父组件事件
      this.$emit('input', this.selectedProvince.id)
      this.$emit('change', {
        id: this.selectedProvince.id,
        name: this.selectedProvince.name
      })
      
      this.dialogVisible = false
    },
    
    // 弹窗关闭时的处理
    handleDialogClosed() {
      this.currentSelected = null
      window.removeEventListener('resize', this.handleResize)
      if (this.mapChart) {
        this.mapChart.off('click', this.handleMapClick)
      }
    },
    
    // 窗口大小变化时重绘地图
    handleResize() {
      if (this.mapChart) {
        setTimeout(() => {
          this.mapChart.resize()
        }, 100)
      }
    }
  }
}
</script>

<style scoped>
/* 样式保持不变 */
.province-map-selector {
  display: inline-block;
}

.map-container {
  position: relative;
  width: 100%;
  height: 500px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.map-chart {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.selected-info {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #ebeef5;
}

.hint-info {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  z-index: 100;
}

.confirm-btn {
  margin-left: 8px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .map-container {
    height: 400px;
  }
  
  .selected-info {
    top: 5px;
    right: 5px;
    flex-direction: column;
    align-items: flex-end;
    padding: 6px 10px;
  }
  
  .hint-info {
    bottom: 5px;
    left: 5px;
    right: 5px;
  }
}
</style>